import * as React from "react"
import { cn } from "@/utils"
import { Loader2 } from "lucide-react"

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" | "success"
  size?: "default" | "sm" | "lg" | "icon" | "xs"
  loading?: boolean
  icon?: React.ReactNode
  iconPosition?: "left" | "right"
  fullWidth?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    className,
    variant = "default",
    size = "default",
    loading = false,
    icon,
    iconPosition = "left",
    fullWidth = false,
    children,
    disabled,
    ...props
  }, ref) => {
    const isDisabled = disabled || loading
    
    return (
      <button
        className={cn(
          "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
          {
            "bg-primary text-primary-foreground hover:bg-primary/90": variant === "default",
            "bg-destructive text-destructive-foreground hover:bg-destructive/90": variant === "destructive",
            "bg-green-600 text-white hover:bg-green-700": variant === "success",
            "border border-input bg-background hover:bg-accent hover:text-accent-foreground": variant === "outline",
            "bg-secondary text-secondary-foreground hover:bg-secondary/80": variant === "secondary",
            "hover:bg-accent hover:text-accent-foreground": variant === "ghost",
            "text-primary underline-offset-4 hover:underline": variant === "link",
          },
          {
            "h-8 px-3 text-xs": size === "xs",
            "h-9 rounded-md px-3": size === "sm",
            "h-10 px-4 py-2": size === "default",
            "h-11 rounded-md px-8": size === "lg",
            "h-10 w-10": size === "icon",
          },
          fullWidth && "w-full",
          className
        )}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading && (
          <Loader2 className={cn(
            "h-4 w-4 animate-spin",
            iconPosition === "left" ? "mr-2" : "ml-2",
            children ? "" : "mx-auto"
          )} />
        )}
        
        {!loading && icon && iconPosition === "left" && (
          <span className="mr-2">{icon}</span>
        )}
        
        {children && <span>{children}</span>}
        
        {!loading && icon && iconPosition === "right" && (
          <span className="ml-2">{icon}</span>
        )}
      </button>
    )
  }
)
Button.displayName = "Button"

export interface ButtonGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  orientation?: "horizontal" | "vertical"
  spacing?: "none" | "sm" | "md" | "lg"
}

const ButtonGroup = React.forwardRef<HTMLDivElement, ButtonGroupProps>(
  ({ className, children, orientation = "horizontal", spacing = "md", ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex",
          orientation === "horizontal" ? "flex-row" : "flex-col",
          {
            "space-x-0": spacing === "none",
            "space-x-2": spacing === "sm" && orientation === "horizontal",
            "space-x-4": spacing === "md" && orientation === "horizontal",
            "space-x-6": spacing === "lg" && orientation === "horizontal",
            "space-y-0": spacing === "none",
            "space-y-2": spacing === "sm" && orientation === "vertical",
            "space-y-4": spacing === "md" && orientation === "vertical",
            "space-y-6": spacing === "lg" && orientation === "vertical",
          },
          className
        )}
        {...props}
      >
        {React.Children.map(children, (child, index) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child as React.ReactElement<any>, {
              className: cn(
                orientation === "horizontal" && index > 0 && "rounded-l-none",
                orientation === "horizontal" && index < React.Children.count(children) - 1 && "rounded-r-none",
                orientation === "vertical" && index > 0 && "rounded-t-none",
                orientation === "vertical" && index < React.Children.count(children) - 1 && "rounded-b-none",
                child.props.className
              )
            })
          }
          return child
        })}
      </div>
    )
  }
)
ButtonGroup.displayName = "ButtonGroup"

export { Button, ButtonGroup }
